import { useState } from 'react';

import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useGetChunkV2 } from '@/api/queries/useGetChunkV2';
import { ChunkDocViewer } from '@/new/components/doc-viewer/chunk-doc-viewer';
import { Icon } from '@/new/components/icon';
import { DocumentTabs } from '@/new/components/reference-content/document-tabs';
import { DrawerErrorState } from '@/new/components/reference-content/drawer-error-state';
import { Button } from '@/new/components/shadcn/button';
import { cn } from '@/new/lib/utils';

interface ChunkDocumentDrawerContentProps {
  chunkId: string;
}

export function ChunkDocumentDrawerContent({ chunkId }: ChunkDocumentDrawerContentProps) {
  const { matterId } = useParams();
  const { data: chunk, isLoading, error } = useGetChunkV2(matterId, chunkId);
  const [isInfoPanelCollapsed, setIsInfoPanelCollapsed] = useState(false);

  return (
    <div className="flex h-full flex-col">
      {error ? (
        <DrawerErrorState label="chunk" />
      ) : (
        <>
          {isLoading && (
            <div className="my-20 flex h-full w-full items-center justify-center">
              <Icon className="lucide--loader-2 animate-spin duration-700" />
            </div>
          )}
          {chunk && (
            <>
              {/* Document Viewer Section */}
              <div className="-mb-8 flex-1 overflow-hidden" onClick={() => setIsInfoPanelCollapsed(true)}>
                <ChunkDocViewer docId={chunk.docId} chunkId={chunk.id} boundingBoxes={chunk.boundingBoxes} />
              </div>

              <div className="z-10 max-h-9/10 overflow-hidden" onMouseEnter={() => setIsInfoPanelCollapsed(false)}>
                <DocumentTabs
                  tabs={[{ id: chunk.docId, label: chunk.doc.file.name }]}
                  selectedId={chunk.docId}
                  onSelect={() => {}}
                />

                <motion.div
                  className="relative z-20 -mt-px overflow-hidden border-t bg-white text-xs shadow-[0_-1px_8px_rgba(0,0,0,0.06)]"
                  animate={{ height: isInfoPanelCollapsed ? '16px' : 'auto' }}
                  transition={{ duration: 0.2 }}
                >
                  <div className={cn('mt-2 max-h-[calc(90vh-3rem)] overflow-y-auto px-5')}>
                    <div className="my-2 flex items-center gap-1.5">
                      <p className="font-medium">Source Text</p>
                      <Button
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(chunk.content);
                          toast.success('Source text copied to clipboard');
                        }}
                        className="ml-1 px-1.5 py-0.75"
                      >
                        <Icon className="lucide--copy" />
                        <span className="text-[11px]">Copy</span>
                      </Button>
                    </div>

                    <p className="mb-4 text-gray-700">{chunk.content}</p>
                  </div>
                </motion.div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
